import { TOKEN_EXPIRY_MINUTES } from '@/lib/authConstants';
import api from './core';
import { User } from './types';
import axios from 'axios';

interface LoginResponse {
  access: string;
  refresh: string;
}

interface UserResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_superuser: boolean;
  // Exposed by UserMeSchema; StaffGuard treats a missing value as false.
  is_staff?: boolean;
  last_active_customer?: string;
  customer_access: CustomerAccess[];
  profile?: {
    theme?: 'dark' | 'light' | 'auto';
    last_active_customer?: string;
    last_active_site?: number | null;
  };
}

/** One row of ``/crm/users/me/`` → ``customer_access``.
 *
 *  Matches ``CustomerAccessListSchema``: a flat ``customer_id`` and ``admin``.
 *  It previously declared ``{ customer, is_admin }``, which exists nowhere in
 *  the payload — anything reading those fields silently got ``undefined``. */
interface CustomerAccess {
  id: number;
  customer_id: number;
  admin: boolean;
}

export const getTokenPair = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>(`/token/pair`, {
    username: email, // Using email as username
    password,
  });

  // Store tokens and set expiry
  localStorage.setItem('access_token', response.data.access);
  localStorage.setItem('refresh_token', response.data.refresh);
  
  // Set token expiry (must match backend ACCESS_TOKEN_LIFETIME minus buffer)
  const tokenExpiry = new Date();
  tokenExpiry.setMinutes(tokenExpiry.getMinutes() + TOKEN_EXPIRY_MINUTES);
  localStorage.setItem('token_expiry', tokenExpiry.toISOString());

  // Set the Authorization header for subsequent requests
  api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;

  return response.data;
};

interface RefreshTokenOptions {
  active_site_id?: number;
  active_customer_id?: number;
}

export const refreshAuthToken = async (
  refreshToken: string,
  options: RefreshTokenOptions = {},
): Promise<LoginResponse> => {
  const body: Record<string, unknown> = { refresh: refreshToken };
  if (options.active_site_id !== undefined) body.active_site_id = options.active_site_id;
  if (options.active_customer_id !== undefined) body.active_customer_id = options.active_customer_id;

  const response = await api.post<LoginResponse>(`/token/refresh`, body);

  // Store tokens and set expiry
  localStorage.setItem('access_token', response.data.access);
  localStorage.setItem('refresh_token', response.data.refresh);

  // Set token expiry (must match backend ACCESS_TOKEN_LIFETIME minus buffer)
  const tokenExpiry = new Date();
  tokenExpiry.setMinutes(tokenExpiry.getMinutes() + TOKEN_EXPIRY_MINUTES);
  localStorage.setItem('token_expiry', tokenExpiry.toISOString());

  // Set the Authorization header for subsequent requests
  api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;

  return response.data;
};

export const verifyAuthToken = async (token: string): Promise<void> => {
  await api.post(`/token/verify`, {
    token,
  });
};

// Single-flight: the auth flow can call this from a few places on load
// (login + init paths). If a /me request is already in flight, hand back the
// same promise instead of firing another — collapses the duplicate fetches.
let inflightMe: Promise<UserResponse> | null = null;

/** ``quiet``: no "server error" toast — for the start-up session check, whose
 *  own screen explains an unreachable backend (#433). */
export const getCurrentUser = async (opts: { quiet?: boolean } = {}): Promise<UserResponse> => {
  if (inflightMe) return inflightMe;
  inflightMe = api
    .get<UserResponse>(`/crm/users/me/`, opts.quiet ? { skipErrorToast: true } : undefined)
    .then((response) => response.data)
    .finally(() => {
      inflightMe = null;
    });
  return inflightMe;
};

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  return getTokenPair(email, password);
};
